import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiZap, FiCamera, FiHome, FiSun, FiShield, FiArrowRight } = FiIcons;

const ServicesPreview = () => {
  const services = [
    {
      icon: FiZap,
      title: 'Electrical Installation',
      description: 'Complete electrical wiring, maintenance, and repair services for residential and commercial properties.',
      color: 'from-yellow-500 to-orange-500'
    },
    {
      icon: FiCamera,
      title: 'CCTV & Security',
      description: 'Professional CCTV installation, monitoring systems, and comprehensive security solutions.',
      color: 'from-blue-500 to-cyan-500'
    },
    {
      icon: FiHome,
      title: 'Smart Home Automation',
      description: 'Transform your home with intelligent automation systems for lighting, climate, and security.',
      color: 'from-purple-500 to-pink-500'
    },
    {
      icon: FiSun,
      title: 'Solar Power Systems',
      description: 'Eco-friendly solar installations and renewable energy solutions for sustainable living.',
      color: 'from-green-500 to-emerald-500'
    },
    {
      icon: FiShield,
      title: 'Access Control',
      description: 'Advanced access control systems and security infrastructure for enhanced protection.',
      color: 'from-red-500 to-rose-500'
    },
    {
      icon: FiZap,
      title: 'Inverter & UPS',
      description: 'Reliable power backup solutions with professional inverter and UPS installation services.',
      color: 'from-indigo-500 to-blue-500'
    }
  ];

  return (
    <section className="py-20 bg-gradient-to-b from-gray-900 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-4">
            <span className="gradient-text">Our Expert Services</span>
          </h2>
          <p className="text-xl text-gray-400 max-w-3xl mx-auto">
            From basic electrical work to advanced smart home automation, we provide comprehensive solutions tailored to your needs.
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {services.map((service, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative bg-gradient-to-br from-gray-800/50 to-gray-900/50 backdrop-blur-sm rounded-2xl p-8 border border-gray-700/50 hover:border-blue-500/50 transition-all duration-500 hover:transform hover:scale-105"
            >
              {/* Gradient Background Effect */}
              <div className={`absolute inset-0 bg-gradient-to-br ${service.color} opacity-0 group-hover:opacity-10 rounded-2xl transition-opacity duration-500`} />
              
              {/* Icon */}
              <div className="relative mb-6">
                <div className={`w-16 h-16 bg-gradient-to-br ${service.color} rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300`}>
                  <SafeIcon 
                    icon={service.icon} 
                    className="text-2xl text-white" 
                  />
                </div>
              </div>

              {/* Content */}
              <div className="relative">
                <h3 className="text-xl font-bold text-white mb-3 group-hover:text-blue-300 transition-colors duration-300">
                  {service.title}
                </h3>
                <p className="text-gray-400 leading-relaxed">
                  {service.description}
                </p>
              </div>

              {/* Hover Effect */}
              <div className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none">
                <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-blue-600/10 to-cyan-600/10" />
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA to Services Page */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <Link
            to="/services"
            className="group inline-flex items-center space-x-3 bg-gradient-to-r from-blue-600 to-cyan-600 px-8 py-4 rounded-full text-white font-semibold text-lg hover:from-blue-700 hover:to-cyan-700 transition-all duration-300 glow-blue"
          >
            <span>View All Services</span>
            <SafeIcon 
              icon={FiArrowRight} 
              className="text-lg group-hover:translate-x-1 transition-transform duration-300" 
            />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default ServicesPreview;